import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { type FC, useCallback, useContext, useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';

import { Clipboard, PencilSquare, Trash } from 'react-bootstrap-icons';
import { config } from '../../../modules/config';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import {
  useAuth,
  useLoading,
  useNotifications,
  useRequest,
} from '../../../modules/hooks';
import type { RoomResponse } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { Can } from '../../../modules/utils/can';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import { CanMulti } from '../../CanMulti';
import { DotDropDown } from '../../Customize/DropDown';
import { RoomEditorModal } from '../../Modals/RoomEditor';
import { DeleteRoomModal } from '../../Modals/RoomEditor/DeleteRoomModal';
import { DataTable, ImageLabelCellRenderer } from '../DataTable';

interface RoomDotCellRendererProps {
  data?: RoomResponse;
  onClickDeleteRoom: (data?: RoomResponse) => void;
  onClickEditRoom: (data?: RoomResponse) => void;
  onClickDuplicateRoom: (data?: RoomResponse) => void;
}

const RoomDotMenu: FC<RoomDotCellRendererProps> = ({
  data,
  onClickDeleteRoom,
  onClickEditRoom,
  onClickDuplicateRoom,
}) => {
  return (
    <CanMulti actions={['create', 'update', 'delete']} subject="rooms">
      <DotDropDown title="" drop="end">
        <Can I="update" a="rooms">
          <Dropdown.Item
            onClick={() => {
              onClickEditRoom(data);
            }}
          >
            <PencilSquare className="dropdown-icon dropdown-icon-margin" /> Edit Room
          </Dropdown.Item>
        </Can>

        <Can I="create" a="rooms">
          <Dropdown.Item
            onClick={() => {
              onClickDuplicateRoom(data);
            }}
          >
            <Clipboard className="dropdown-icon dropdown-icon-margin" /> Duplicate Room
          </Dropdown.Item>
        </Can>

        <Can I="delete" a="rooms">
          <Dropdown.Item
            onClick={() => {
              onClickDeleteRoom(data);
            }}
          >
            <Trash className="dropdown-icon dropdown-icon-margin" /> Delete Room
          </Dropdown.Item>
        </Can>
      </DotDropDown>
    </CanMulti>
  );
};

const getDotMenu = ({
  onClickDeleteRoom,
  onClickEditRoom,
  onClickDuplicateRoom,
}: RoomDotCellRendererProps) => {
  const DotMenuCellRenderer: FC<ICellRendererParams<RoomResponse>> = (
    params: ICellRendererParams<RoomResponse>,
  ) => {
    const { data } = params;

    return (
      <RoomDotMenu
        data={data}
        onClickDeleteRoom={onClickDeleteRoom}
        onClickEditRoom={onClickEditRoom}
        onClickDuplicateRoom={onClickDuplicateRoom}
      />
    );
  };

  return DotMenuCellRenderer;
};

export const RoomsTable: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const [editData, setEditData] = useState<RoomResponse>();
  const [updateUId, setUpdateUId] = useState<string>();
  const [editModal, setEditModal] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [tableData, setTableData] = useState<RoomResponse[]>([]);
  const { setLoading } = useLoading()!;
  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const [{ data, loading, error }, refetchRooms] = useRequest<RoomResponse[]>(
    endpoints.ROOMS,
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
  );

  const handleAddRoom = () => {
    setUpdateUId('');
    setEditData(undefined);
    setEditModal(true);
  };

  const onClickEditRoom = useCallback(
    (room?: RoomResponse) => {
      if (room) {
        setUpdateUId(room.uuid);
        setEditData(room);
        setEditModal(true);
      }
    },
    [setUpdateUId, setEditData, setEditModal],
  );

  const onClickDuplicateRoom = useCallback(
    (room?: RoomResponse) => {
      if (room) {
        setUpdateUId(room.uuid);
        setDuplicate(true);
        setEditData(room);
        setEditModal(true);
      }
    },
    [setUpdateUId, setEditData, setEditModal],
  );

  const onClickDeleteRoom = useCallback(
    (room?: RoomResponse) => {
      if (room) {
        setUpdateUId(room.uuid);
        setDeleteModal(true);
      }
    },
    [setUpdateUId],
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    if (error) {
      const message = getApiErrorMessage(error);
      setSimpleToasts({ type: 'danger', message, show: true });
      const mockArray = Array(10);
      const mockData: RoomResponse[] = mockArray.fill(0, 0, 10).map(() => ({
        uuid: '',
        name: 'Room ABC',
        description: 'Room ABC',
        abbreviation: 'ABC',
        checkInSteps: 'string',
        roomSize: '20',
        maxOccupants: '20',
      }));
      setTableData(mockData);
    }

    if (data) {
      setTableData(data.result);
    }
  }, [loading, data, error, setSimpleToasts, setLoading]);

  const refreshRooms = useCallback(async () => {
    await refetchRooms();
  }, [refetchRooms]);

  const [columns] = useState<ColDef<RoomResponse>[]>([
    {
      valueGetter: (params) => {
        const { data: room } = params;
        const name = room ? `${room.name}` : '-';
        return { name, img: room?.media?.[0]?.uri || 'Icon-room.svg' };
      },
      cellRenderer: ImageLabelCellRenderer,
      headerName: 'Room name',
      flex: 2,
    },
    {
      field: 'abbreviation',
      headerName: 'Abbreviation',
    },
    {
      field: 'roomSize',
      headerName: 'Room size',
    },
    {
      field: 'bedType',
      headerName: 'Bed type',
    },
    {
      field: 'maxOccupants',
      headerName: 'Max occupants',
    },
    {
      field: 'property.name',
      headerName: 'Property',
      flex: 2,
    },

    {
      headerName: 'ACTION',
      colId: 'action',

      maxWidth: 100,
      cellRenderer: getDotMenu({
        onClickDeleteRoom,
        onClickEditRoom,
        onClickDuplicateRoom,
      }),
      cellStyle: { overflow: 'visible' },
    },
  ]);

  return (
    <>
      <DataTable
        addButtonFunction={handleAddRoom}
        addButtonText="Add Room"
        component="rooms"
        refreshHandler={refreshRooms}
        changeState={0}
        rowData={tableData}
        columnDefs={columns}
        pagination
        paginationPageSize={config.tables.balances.paginationSize}
      />
      <RoomEditorModal
        show={editModal}
        duplicate={duplicate}
        handleClose={() => {
          setEditModal(false);
          setEditData(undefined);
          setDuplicate(false);
        }}
        data={editData}
        refetchRooms={refetchRooms}
      />
      {updateUId && (
        <DeleteRoomModal
          show={deleteModal}
          handleClose={() => {
            setDeleteModal(false);
          }}
          roomId={updateUId}
          refetchRooms={refetchRooms}
        />
      )}
    </>
  );
};
